import { FatalError } from "@prismicio/editor-support/Error"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { preload, useRequest } from "@prismicio/editor-support/Suspense"
import { z } from "zod"

import { env } from "../env"
import { getRepositoryName } from "../routing"

const role = z.enum(["Owner", "Administrator", "Writer", "Manager", "Readonly"])
const rolesPerLocale = z.record(role.optional())
const repositoryRole = role.or(rolesPerLocale)

const roleMap = {
  SuperUser: "SuperUser",
  Owner: "Owner",
  Administrator: "Administrator",
  Manager: "Publisher",
  Writer: "Writer",
  Readonly: "Contributor"
} as const

export type Role = typeof roleMap[keyof typeof roleMap]
export type RepositoryRole = z.TypeOf<typeof repositoryRole>

export const profileResponse = z.object({
  shortId: z.string(),
  intercomHash: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  superPower: z.boolean(),
  repositories: z.array(
    z.object({
      domain: z.string(),
      role: repositoryRole
    })
  )
})

// Have to type this manually because z.TypeOf is bugged for profileResponse / objects with an union value.
// can remove when https://github.com/colinhacks/zod/issues/2654 is fixed.
// Should be fixed once https://github.com/colinhacks/zod/pull/3138 PR is merged.
export interface Profile {
  shortId: string
  intercomHash: string
  email: string
  firstName: string
  lastName: string
  superPower: boolean
  repositories: Array<{ domain: string; role: RepositoryRole }>
}

function getProfile(): Promise<Profile> {
  const url = new URL("profile", env.USER_SERVICE_BASE_URL)
  return request(url, {
    credentials: getRequestCredentials(env.PAGE_BUILDER_AUTH_STRATEGY),
    okType: profileResponse
  })
}

export function useProfile() {
  return useRequest(getProfile, [])
}

export function preloadProfile() {
  preload(getProfile, [])
}

interface UseRoleResult {
  role: Role
  isContributor: boolean
  isWriter: boolean
  isSuperUser: boolean
}

export function useRole(locale: string): UseRoleResult {
  const { repositories, superPower } = useProfile()

  if (superPower === true)
    return {
      role: "SuperUser",
      isContributor: false,
      isWriter: false,
      isSuperUser: true
    }

  const repositoryName = getRepositoryName()
  const repository = repositories.find(
    (repository) => repository.domain === repositoryName
  )

  if (!repository)
    throw new FatalError("User does not have access to this repository")

  const serverRole =
    typeof repository.role === "string"
      ? repository.role
      : repository.role[locale]

  const role = roleMap[serverRole ?? "Readonly"]

  const isContributor = role === "Contributor"
  const isWriter = role === "Writer"

  return {
    role,
    isContributor,
    isWriter,
    isSuperUser: false
  }
}
